import { useCallback, useRef } from 'react';
import { type UndoResponse, api, undoIdOf } from '../lib/api';
import { isTransient } from '../lib/errors';
import { type ToastAction, useToast } from './ErrorToast';

/**
 * «Отменить» в тосте (003, FR-038; research R9; contracts/ui-panel.md, «Тосты»): после ответа, правки, отмены правки,
 * добавления, копирования и удаления человека тост «… · Отменить · ✕» живёт 6 с. Отмена — `POST /api/actions/:id/undo`.
 * Успех — тост «Отменено» и `onDone` (страница перечитывает табель). Отказ — тост с понятным текстом по коду
 * (`UNDO_NOT_LAST`, `UNDO_STALE`, `UNDO_EXPIRED`, `FORBIDDEN`) и тоже `onDone`: данные могли измениться. «Повторить» —
 * только при сбое связи или сервера. Ответ без `undoActionId` (табель без движка) — тост без «Отменить».
 */
export function useUndo({
  screen,
  onDone,
}: {
  /** Экран для журнала ошибок интерфейса. */
  screen: string;
  onDone: (res: UndoResponse) => void;
}): (res: unknown) => ToastAction | undefined {
  const { showInfo, showError } = useToast();
  const done = useRef(onDone);
  done.current = onDone;
  const run = useRef<(id: number) => Promise<void>>(async () => {});
  run.current = async (id: number) => {
    try {
      const res = await api.undo(id);
      showInfo('Отменено');
      done.current(res ?? {});
    } catch (e) {
      const transient = isTransient(e);
      showError(e, {
        screen,
        action: 'undo',
        lead: 'Не отменено',
        retry: transient ? () => void run.current(id) : undefined,
      });
      if (!transient) done.current({});
    }
  };
  return useCallback((res: unknown) => {
    const id = undoIdOf(res);
    return id === null ? undefined : { label: 'Отменить', onClick: () => void run.current(id) };
  }, []);
}
